// Cache Manager Utility
// Centralized cache clearing for client switching and other scenarios

import { campaignCache } from './campaignCache';
import { clearAllStatsCache } from '../services/gameAnalytics';

class CacheManager {
  constructor() {
    this.cacheKeys = {
      // Stats cache keys
      statsCache: 'game_stats_cache_v1',
      
      // Chart data cache keys (stored in localStorage)
      chartData: 'chart_data_',
      chartDataTime: 'chart_data_time_',
      
      // Campaign cache patterns
      campaignPattern: /^campaign_/,
      userCampaignsPattern: /^user_campaigns_/,
      statsPattern: /^stats_/,
      eventsPattern: /^events_/,
      chartPattern: /^chart_/
    };
  }

  // Clear all caches when switching clients
  clearAllCaches() {
    console.log('🧹 Clearing all caches for client switch...');
    
    try {
      // Clear campaign cache (in-memory)
      campaignCache.clear();
      console.log('✅ Campaign cache cleared');
      
      // Clear game stats cache
      clearAllStatsCache();
      console.log('✅ Game stats cache cleared');
      
      // Clear chart data caches (localStorage)
      this.clearChartDataCaches();
      console.log('✅ Chart data caches cleared');
      
      // Clear any other localStorage caches
      this.clearLocalStorageCaches();
      console.log('✅ LocalStorage caches cleared');
      
      console.log('🎉 All caches cleared successfully');
      
    } catch (error) {
      console.error('❌ Error clearing caches:', error);
    }
  }

  // Clear chart data caches from localStorage
  clearChartDataCaches() {
    const keysToRemove = [];
    
    // Find all chart data keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith(this.cacheKeys.chartData) || key.startsWith(this.cacheKeys.chartDataTime))) {
        keysToRemove.push(key);
      }
    }
    
    // Remove found keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    if (keysToRemove.length > 0) {
      console.log(`🗑️ Removed ${keysToRemove.length} chart data cache entries`);
    }
  }

  // Clear other localStorage caches
  clearLocalStorageCaches() {
    const keysToRemove = [];
    
    // Find cache-related keys
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isCacheKey(key)) {
        keysToRemove.push(key);
      }
    }
    
    // Remove found keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
    
    if (keysToRemove.length > 0) {
      console.log(`🗑️ Removed ${keysToRemove.length} localStorage cache entries`);
    }
  }

  // Check if a key is a cache key
  isCacheKey(key) {
    return (
      key.includes('_cache') ||
      key.includes('_stats') ||
      key.includes('_data') ||
      key.includes('_time') ||
      key.startsWith('chart_') ||
      key.startsWith('campaign_') ||
      key.startsWith('user_campaigns_') ||
      key.startsWith('stats_') ||
      key.startsWith('events_') ||
      key.startsWith('game_stats_')
    );
  }

  // Clear caches for specific company/client
  clearCompanyCaches(companyId) {
    console.log(`🧹 Clearing caches for company: ${companyId}`);
    
    try {
      // Clear campaign cache entries for this company
      campaignCache.invalidatePattern(`user_campaigns_${companyId}`);
      campaignCache.invalidatePattern(`campaign_.*`);
      campaignCache.invalidatePattern(`stats_.*`);
      campaignCache.invalidatePattern(`events_.*`);
      campaignCache.invalidatePattern(`chart_.*`);
      
      // Clear game stats cache
      clearAllStatsCache();
      
      // Clear chart data caches
      this.clearChartDataCaches();
      
      console.log(`✅ Caches cleared for company: ${companyId}`);
      
    } catch (error) {
      console.error(`❌ Error clearing caches for company ${companyId}:`, error);
    }
  }

  // Get cache statistics for debugging
  getCacheStats() {
    return {
      campaignCache: campaignCache.getStats(),
      localStorageKeys: this.getLocalStorageCacheKeys()
    };
  }

  // Get all cache-related localStorage keys
  getLocalStorageCacheKeys() {
    const cacheKeys = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && this.isCacheKey(key)) {
        cacheKeys.push(key);
      }
    }
    return cacheKeys;
  }

  // Clear specific cache type
  clearCacheType(type) {
    switch (type) {
      case 'campaign':
        campaignCache.clear();
        break;
      case 'gameStats':
        clearAllStatsCache();
        break;
      case 'chartData':
        this.clearChartDataCaches();
        break;
      case 'localStorage':
        this.clearLocalStorageCaches();
        break;
      default:
        console.warn(`Unknown cache type: ${type}`);
    }
  }
}

// Create singleton instance
const cacheManager = new CacheManager();

export default cacheManager;
